import { useState, useId, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, UserPlus, CheckCircle2, Sparkles, ShieldCheck, Copy, Check, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import logoUrbnet from "@/assets/images/UrbNetLogoSemFundo.png";
import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { autenticacaoService } from "@/services/autenticacaoService";
import { OUVIDORIA_ENDPOINTS } from "@/config/ouvidoria";
import { AppConfig } from "../datasources/AppConfig";
import { encryptId, encryptToken, decryptId } from "@/utils/tokenCrypto";

const FloatingOrb = ({ className, delay = 0 }: { className?: string; delay?: number }) => (
  <motion.div
    className={`absolute rounded-full blur-3xl opacity-30 ${className}`}
    animate={{
      y: [0, -30, 0],
      x: [0, 15, 0],
      scale: [1, 1.1, 1],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  />
);

type ModoCadastro = 'self' | 'admin';

// Valida se uma string está no formato GUID (8-4-4-4-12)
const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const isGuid = (v: unknown): v is string => typeof v === "string" && GUID_RE.test(v.trim());

/**
 * Procura o GUID do usuário na resposta da API de forma resiliente.
 * Prioriza um objeto `usuario`/`user` com `id` em formato GUID, depois campos
 * `usuarioId`/`userId`. Como último recurso, usa o primeiro `id` GUID encontrado
 * (normalmente o id do agente). NUNCA retorna valores que não sejam GUID, o que
 * impede que o nome do agente acabe sendo usado no lugar do id.
 */
function extrairUsuarioId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;

  let fallbackId: string | undefined;
  const visited = new Set<object>();
  const stack: unknown[] = [payload];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current || typeof current !== "object" || visited.has(current as object)) continue;
    visited.add(current as object);
    const obj = current as Record<string, unknown>;

    // 1) objeto usuario/user com id em formato GUID
    const usuario = obj.usuario ?? obj.user;
    if (usuario && typeof usuario === "object") {
      const uid = (usuario as Record<string, unknown>).id;
      if (isGuid(uid)) return uid;
    }

    // 2) usuarioId / userId no nível atual
    if (isGuid(obj.usuarioId)) return obj.usuarioId as string;
    if (isGuid(obj.userId)) return obj.userId as string;

    // 3) guarda o primeiro `id` GUID como fallback (provável agenteId)
    if (fallbackId === undefined && isGuid(obj.id)) {
      fallbackId = obj.id as string;
    }

    // explora objetos aninhados
    for (const value of Object.values(obj)) {
      if (value && typeof value === "object") stack.push(value);
    }
  }

  return fallbackId;
}

export default function CadastroAgentePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const navState = (location.state as { modoCadastro?: ModoCadastro; novaOuvidoriaId?: string } | null) || null;
  const modoCadastro: ModoCadastro = navState?.modoCadastro === 'admin' ? 'admin' : 'self';
  const isAdmin = modoCadastro === 'admin';
  const novaOuvidoriaId = navState?.novaOuvidoriaId;
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [nextRoute, setNextRoute] = useState<string>("");
  const [linkCopied, setLinkCopied] = useState(false);
  const gradientId = useId();

  // Em modo self, recupera ouvidoriaId do localStorage caso AppConfig esteja vazio.
  // Em modo admin, o ouvidoriaId vem via location.state e não devemos tocar AppConfig.
  useEffect(() => {
    const loadOuvidoriaId = async () => {
      if (!isAdmin && !AppConfig.ouvidoriaId) {
        const savedOuvidoriaIdCifrado = localStorage.getItem('ouvidoriaId');
        if (savedOuvidoriaIdCifrado) {
          const savedOuvidoriaId = await decryptId(savedOuvidoriaIdCifrado);
          if (savedOuvidoriaId) {
            AppConfig.ouvidoriaId = savedOuvidoriaId;
          }
        }
      }
    };
    loadOuvidoriaId();
  }, [isAdmin]);

  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
  });

  const updateField = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nome || !form.email) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const dataSource = new RemoteDataSource(Settings.urlBase);
      
      // Em modo admin: ouvidoriaId vem do fluxo de criação supervisionada (state),
      // sem alterar AppConfig (gestor permanece logado na sua ouvidoria geral).
      // Em modo self: usa AppConfig (com fallback para localStorage).
      let ouvidoriaId = isAdmin ? (novaOuvidoriaId || '') : AppConfig.ouvidoriaId;
      if (!isAdmin && !ouvidoriaId) {
        const ouvidoriaIdCifrado = localStorage.getItem('ouvidoriaId');
        if (ouvidoriaIdCifrado) {
          ouvidoriaId = await decryptId(ouvidoriaIdCifrado);
          if (ouvidoriaId) {
            AppConfig.ouvidoriaId = ouvidoriaId;
          }
        }
      }

      if (!ouvidoriaId) {
        throw new Error('ID da ouvidoria não encontrado. Por favor, crie a ouvidoria novamente.');
      }

      // 1) Cria o USUÁRIO na API de autenticação (POST /autenticacao/v1/usuarios).
      // O `id` do usuário é o e-mail (conforme o contrato "Criar usuário").
      // É daqui que extraímos o GUID do usuário (usuario.id) usado no link de ativação.
      const usuarioResult = await autenticacaoService.criarUsuario({
        id: form.email,
        nome: form.nome,
      });

      if ('failure' in usuarioResult) {
        toast({
          title: "Erro ao criar usuário",
          description: usuarioResult.failure.message || "Não foi possível criar o usuário. Tente novamente.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const usuarioIdCriado = usuarioResult.success.usuario?.id;
      const tokenAcesso = usuarioResult.success.tokenAcesso;

      // 2) Cria o AGENTE vinculado à ouvidoria.
      // A requisição é autenticada com o tokenAcesso retornado na criação do usuário
      // (e não com o token do usuário logado).
      const formData = new FormData();
      formData.append("nome", form.nome);
      if (form.email) formData.append("email", form.email);
      if (form.telefone) formData.append("telefone", form.telefone);

      // // Marcar como gestor da ouvidoria (primeiro agente)
      // if(){}
      // formData.append("gestor", "true");

      const result = await dataSource.post({
        endpoint: `v1/ouvidorias/${ouvidoriaId}/agentes`,
        requiredToken: false,
        headers: {
          "Accept": "application/force+json",
          "Authorization": `bearer ${tokenAcesso}`,
        },
        body: formData as unknown as Record<string, unknown>,
      });

      if ('failure' in result) {
        toast({
          title: "Erro ao criar conta",
          description: result.failure.messages.join(", ") || "Ocorreu um erro ao criar sua conta. Tente novamente.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // Extrai o id do agente (campo `id`/`agenteId` no topo da resposta)
      type CriacaoResponse = { id?: string; agenteId?: string };
      const responseBody = result.success.body as CriacaoResponse | undefined;
      const agenteId = isGuid(responseBody?.id)
        ? responseBody?.id
        : (isGuid(responseBody?.agenteId) ? responseBody?.agenteId : undefined);

      // GUID do usuário: usa o id retornado na CRIAÇÃO DO USUÁRIO; se por algum
      // motivo vier vazio, faz fallback resiliente na resposta do agente.
      // Sempre validado como GUID (impede que o nome acabe no link).
      const usuarioId = isGuid(usuarioIdCriado)
        ? usuarioIdCriado
        : extrairUsuarioId(result.success.body);

      // Encriptar dados (usuarioId, email, tokenAcesso) para incluir na URL
      let activationLink = null;
      let activationPath = null;
      if (usuarioId && tokenAcesso) {
        const data = {
          usuarioId,
          email: form.email,
          tokenAcesso
        };
        const encrypted = await encryptToken(JSON.stringify(data));
        activationPath = `/autenticacao-dois-fatores?d=${encodeURIComponent(encrypted)}`;
        activationLink = `${window.location.origin}${activationPath}`;
      }

      if (isAdmin) {
        // Modo admin: gestor da geral apenas cria a ouvidoria + agente.
        // Não sobrescrevemos AppConfig.agenteId (sessão permanece a do gestor).
        // O novo agente fará login posteriormente.
        toast({
          title: "Ouvidoria e agente cadastrados!",
          description: "O novo gestor poderá acessar a plataforma fazendo login.",
        });
        // Após o link compartilhado, leva direto para a ativação carregando o id (guid).
        setNextRoute(activationPath ?? '/ouvidorias-supervisionadas');
      } else {
        // Modo self: salva agenteId no AppConfig e localStorage para a sessão do usuário recém-criado
        if (agenteId) {
          AppConfig.agenteId = agenteId;
          localStorage.setItem('agenteId', agenteId);
        }

        toast({
          title: "Conta criada com sucesso!",
          description: "Bem-vindo à equipe!",
        });
        // Após o link compartilhado, leva direto para a ativação carregando o id (guid).
        setNextRoute(activationPath ?? ("/?welcome=true&name=" + encodeURIComponent(form.nome)));
      }

      // Se obtivemos o GUID do usuário, exibe o dialog com o link de compartilhamento.
      // A navegação ocorre quando o gestor fechar/continuar no dialog.
      if (activationLink) {
        setShareLink(activationLink);
      } else {
        navigate(isAdmin ? '/ouvidorias-supervisionadas' : "/?welcome=true&name=" + encodeURIComponent(form.nome));
      }
    } catch (error) {
      toast({
        title: "Erro ao criar conta",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      toast({
        title: "Link copiado!",
        description: "Compartilhe o link com o novo agente para ativar a conta.",
      });
      setTimeout(() => setLinkCopied(false), 2500);
    } catch (err) {
      toast({
        title: "Não foi possível copiar",
        description: "Copie o link manualmente.",
        variant: "destructive",
      });
    }
  };

  const handleContinue = () => {
    const route = nextRoute;
    setShareLink(null);
    if (route) navigate(route);
  };

  const formFields = [
    { id: "nome", label: "Nome completo", placeholder: "Seu nome completo", type: "text", required: true, icon: "👤" },
    { id: "email", label: "E-mail", placeholder: "seu.email@exemplo.com", type: "email", required: true, icon: "✉️" },
    { id: "telefone", label: "Telefone", placeholder: "(11) 99999-9999", type: "tel", required: false, icon: "📱" },
  ];

  const features = [
    { icon: "🎯", title: "Gestão Eficiente", desc: "Gerencie solicitações com agilidade" },
    { icon: "📊", title: "Métricas em Tempo Real", desc: "Acompanhe SLA e indicadores" },
    { icon: "🤝", title: "Trabalho em Equipe", desc: "Colabore com sua equipe" },
    { icon: "🏆", title: "Excelência no Atendimento", desc: "Transforme a gestão pública" },
  ];

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gradient-to-br from-background via-background to-background">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <FloatingOrb className="w-96 h-96 bg-primary/40 -top-48 -left-48" delay={0} />
        <FloatingOrb className="w-80 h-80 bg-secondary/40 top-1/3 -right-40" delay={2} />
        <FloatingOrb className="w-64 h-64 bg-primary/30 bottom-0 left-1/4" delay={4} />
        
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `
              linear-gradient(hsl(var(--primary)) 1px, transparent 1px),
              linear-gradient(90deg, hsl(var(--primary)) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Left Panel - Desktop */}
      <div className="hidden lg:flex flex-col justify-center items-center flex-1 relative z-10 px-16">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-lg"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-8"
          >
            <img 
              src={logoUrbnet} 
              alt="UrbNet Logo" 
              className="h-20 object-contain"
            />
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.15), hsl(var(--secondary) / 0.15))" }}
          >
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Junte-se à nossa equipe
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="font-heading text-5xl font-bold tracking-tight leading-tight mb-6"
          >
            <span className="text-foreground">Bem-vindo à </span>
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            >
              equipe!
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg text-muted-foreground leading-relaxed mb-10"
          >
            Crie sua conta para começar a atender as demandas dos cidadãos
            e fazer a diferença na sua cidade.
          </motion.p>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.02, y: -2 }}
                className="group p-4 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm hover:border-primary/30 hover:bg-card/50 transition-all duration-300"
              >
                <span className="text-2xl mb-2 block">{feature.icon}</span>
                <h3 className="font-semibold text-foreground text-sm mb-1">{feature.title}</h3>
                <p className="text-xs text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="relative">
            {/* Card glow effect */}
            <div 
              className="absolute -inset-1 rounded-3xl blur-xl opacity-20"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
            />
            
            <div className="relative p-8 rounded-3xl border border-border/50 bg-card/80 backdrop-blur-xl shadow-2xl">
              {/* Mobile Logo */}
              <div className="lg:hidden flex justify-center mb-6">
                <img 
                  src={logoUrbnet} 
                  alt="UrbNet Logo" 
                  className="h-16 object-contain"
                />
              </div>

              {/* Header */}
              <div className="mb-8">
                <h2 className="font-heading text-2xl font-bold text-foreground">
                  Criar sua conta
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Preencha seus dados para começar
                </p>
              </div>

              {/* Card de informação - Agente Gestor */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-6"
              >
                <Card className="bg-blue-50 border-blue-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-blue-900 text-base">
                      <ShieldCheck className="h-4 w-4" />
                      Agente Gestor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-blue-800 text-xs">
                      Este agente será o <strong>agente gestor</strong> da ouvidoria, com permissões administrativas completas.
                      Por ser o primeiro agente cadastrado, ele terá acesso a todas as funcionalidades de gestão da ouvidoria.
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {formFields.map((field, index) => (
                  <motion.div
                    key={field.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.4 }}
                    className="space-y-2"
                  >
                    <Label htmlFor={field.id} className="text-sm font-medium flex items-center gap-2">
                      <span>{field.icon}</span>
                      {field.label}
                      {field.required && <span className="text-destructive">*</span>}
                    </Label>
                    <div className="relative">
                      <Input
                        id={field.id}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={form[field.id as keyof typeof form] as string}
                        onChange={(e) => updateField(field.id, e.target.value)}
                        onFocus={() => setFocusedField(field.id)}
                        onBlur={() => setFocusedField(null)}
                        className={`h-12 rounded-xl transition-all duration-300 ${
                          focusedField === field.id 
                            ? "ring-2 ring-primary/50 border-primary" 
                            : "border-border/50"
                        }`}
                      />
                      <AnimatePresence>
                        {form[field.id as keyof typeof form] && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0 }}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            <CheckCircle2 className="h-5 w-5 text-success" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}


                {/* Submit Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="pt-2"
                >
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="relative w-full h-14 rounded-xl font-semibold text-base overflow-hidden group"
                    style={{
                      background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--secondary)))",
                    }}
                  >
                    <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {isLoading ? (
                      <motion.div
                        className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <span className="relative flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        Criar Conta
                      </span>
                    )}
                  </Button>
                </motion.div>
              </form>

              {/* Footer */}
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.4 }}
                className="text-xs text-muted-foreground text-center mt-6"
              >
                Ao criar sua conta, você concorda com os{" "}
                <Link to="/termos-uso" className="text-primary hover:underline">termos de uso</Link>
                {" "}da plataforma.
              </motion.p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* SVG Gradient Definition */}
      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" />
            <stop offset="100%" stopColor="hsl(var(--secondary))" />
          </linearGradient>
        </defs>
      </svg>

      {/* Dialog com o link de ativação para compartilhamento */}
      <Dialog open={!!shareLink} onOpenChange={(open) => { if (!open) handleContinue(); }}>
        <DialogContent preventCloseOnOutsideClick={true}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Link2 className="h-5 w-5 text-primary" />
              </div>
              <DialogTitle>Conta criada! Compartilhe o link de ativação</DialogTitle>
            </div>
            <DialogDescription>
              Envie este link para o novo agente. Ao acessá-lo, ele poderá ativar a conta,
              verificar o código enviado por e-mail e definir a própria senha.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={shareLink ?? ""}
              onFocus={(e) => e.currentTarget.select()}
              className="h-12 rounded-xl text-sm"
            />
            <Button
              type="button"
              onClick={handleCopyLink}
              className="h-12 px-4 rounded-xl shrink-0"
            >
              {linkCopied ? (
                <Check className="h-5 w-5" />
              ) : (
                <Copy className="h-5 w-5" />
              )}
            </Button>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleContinue} className="rounded-xl">
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
